// The rules a Moodle course full name has to follow, and the primitives for reading one.
//
// Check Doc and Fix Format both import from here and nowhere else duplicates these.
// That matters: if the two features ever disagreed about what a valid cohort is, or
// about how a name splits, Fix Format would rewrite cells Check Doc had called clean.
//
// A course full name is tilde-delimited with 8 segments and normally a trailing tilde:
//
//   FDY001~135~414~Self As Learner~Feb26~EW~Gomwe~F26F~
//   1       2   3   4               5     6  7     8
//
//   1 module code   4 module name   5 cohort (MonYY)   8 session code + one group letter
//
// Segments 5 and 8 are different formats on purpose: the cohort spells the month out
// ("Sep25"), the session uses a short code ("S25A").

/** Canonical three-letter months, index 0 = January. */
export const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const FULL_MONTHS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

// The session code map, defined once. Only F, A, S and N occur in live data; J, JN and
// JL exist so January, June and July stay unambiguous if they ever appear, because a
// bare "J" would otherwise be shared by all three.
//
// Mar, May, Aug, Oct and Dec have NO code. That is not an oversight - a session in one
// of those months cannot be expressed, so Fix Format must refuse rather than invent one.
export const CODE_TO_MONTH = { J: "Jan", F: "Feb", A: "Apr", JN: "Jun", JL: "Jul", S: "Sep", N: "Nov" };
export const MONTH_TO_CODE = { jan: "J", feb: "F", apr: "A", jun: "JN", jul: "JL", sep: "S", nov: "N" };

// Longest first, so reading a code off the front of a string takes JN from "JN25"
// rather than J, which would leave the year as "N25".
export const SESSION_CODES = Object.keys(CODE_TO_MONTH).sort((a, b) => b.length - a.length);

export const COHORT_PATTERN = /^([A-Za-z]{3})(\d{2})$/;

// The {1,2} is required. A single-letter pattern would permanently reject the two-letter
// codes JN and JL, and because the "fix" would then equal the original, nothing would be
// written and nothing reported - a silent no-op the user could never clear.
export const SESSION_PATTERN = /^[A-Za-z]{1,2}\d{2}[A-Za-z]$/;

/** Separators people type between a month and a year: "April-25", "F.26.F", "sep 25". */
const SEPARATORS = /[ ._\-/\\]/g;

/**
 * Read any cell value as text, for detection and comparison.
 *
 * Every cell read goes through here. SheetJS hands back plain strings, but a workbook
 * read by another library can yield a RichText object, a Date or a FormulaError, and
 * String()-ing those gives "[object Object]" - which would make every category heading
 * normalise to the same key and collapse all categories into one bucket.
 */
export const cellText = (value) => {
  if (value === null || value === undefined) return "";

  let text;
  if (typeof value === "object") {
    if (typeof value.text === "function") text = value.text();
    else if (value instanceof Date) text = String(value);
    else text = ""; // never "[object Object]"
  } else {
    text = String(value);
  }

  return text.replace(/[\r\n\t]/g, " ").trim();
};

/**
 * Read a cell value for rewriting.
 *
 * Unlike cellText this does not trim. Blank space next to a tilde is itself a problem that
 * Check Doc reports and Fix Format removes, and trimming the cell would hide the space at
 * either end of it.
 */
export const fixText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    if (typeof value.text === "function") return String(value.text()).replace(/[\r\n\t]/g, " ");
    return "";
  }
  return String(value).replace(/[\r\n\t]/g, " ");
};

/** lowercase; "&" -> " and "; non-alphanumerics -> space; collapse; trim. */
export const normalise = (value) =>
  String(value === null || value === undefined ? "" : value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** normalise(), then remove every space. Used for fuzzy name matching. */
export const normaliseTight = (value) => normalise(value).replace(/ /g, "");

/** The key a module name is looked up by: trimmed and lowercased, nothing else. */
export const nameKey = (value) => String(value === null || value === undefined ? "" : value).trim().toLowerCase();

/** Plain Levenshtein edit distance, two-row DP. */
export const levenshtein = (a, b) => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  let previous = Array.from({ length: n + 1 }, (unused, i) => i);

  for (let i = 1; i <= m; i++) {
    const current = [i];
    for (let j = 1; j <= n; j++) {
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    previous = current;
  }

  return previous[n];
};

/** How far a name may be from a known one before the match stops being credible. */
export const fuzzyLimit = (length) => Math.max(1, Math.floor(length * 0.18));

/**
 * Split for rewriting: no trimming, no dropping. Index 3, 4 and 7 get replaced and the
 * pieces are rejoined, so the trailing tilde and any odd internal spacing survive.
 */
export const splitSegments = (value) => String(value).split("~");

/**
 * How many real segments a name has, ignoring the trailing tilde.
 * Pops trailing pieces that are blank, on a copy.
 */
export const segmentCount = (value) => {
  const parts = splitSegments(value);
  while (parts.length > 0 && parts[parts.length - 1].trim() === "") parts.pop();
  return parts.length;
};

/** The 8 segments with the trailing empty dropped, or null when there are not exactly 8. */
export const eightSegments = (value) => {
  const parts = splitSegments(value);
  while (parts.length > 0 && parts[parts.length - 1].trim() === "") parts.pop();
  return parts.length === 8 ? parts : null;
};

/**
 * Blank space next to a tilde, or at either end of the name, piece by piece.
 *
 *   "FDY005~ 136~426~Effective Communication  ~Sep25~Day~Patten~F26A~ "
 *   -> segment 2 has 1 leading, segment 4 has 2 trailing, 1 after the last tilde
 *
 * Reads the untrimmed value, because trimming is exactly what hides the problem. \s also
 * matches non-breaking spaces, which arrive with names pasted from the web.
 * @param {string} raw - the cell as fixText read it
 * @returns {Array<{piece: number, value: string, leading: number, trailing: number}>} piece
 *   is 1-8 for a segment and 9 for anything after the last tilde; an all-blank piece counts
 *   its spaces once, as leading
 */
export const tildeSpacing = (raw) =>
  splitSegments(raw)
    .map((value, i) => {
      const leading = value.length - value.replace(/^\s+/, "").length;
      const trailing =
        leading === value.length ? 0 : value.length - value.replace(/\s+$/, "").length;
      return { piece: Math.min(i + 1, 9), value, leading, trailing };
    })
    .filter((piece) => piece.leading + piece.trailing > 0);

// An initial is a single letter, with or without a full stop, or any abbreviation that
// ends in one: "M", "M." and "Sh." are initials; "Di" and "Enayet" are words.
const isInitial = (word) => /^[A-Za-z]\.?$/.test(word) || word.endsWith(".");

/**
 * Split a tutor name from column E into its surname and whatever comes before it.
 *
 *   "S. Soroosh"    -> { forenames: "S",      surname: "Soroosh", multiWord: false }
 *   "M.Tariq"       -> { forenames: "M",      surname: "Tariq",   multiWord: false }
 *   "M  .Tariq"     -> { forenames: "M",      surname: "Tariq",   multiWord: false }
 *   "M. J. Hasan"   -> { forenames: "MJ",     surname: "Hasan",   multiWord: false }
 *   "Sh. Aijaz"     -> { forenames: "SH",     surname: "Aijaz",   multiWord: false }
 *   "D. Di Emidio"  -> { forenames: "DDI",    surname: "Emidio",  multiWord: true }
 *   "Enayet Sarwar" -> { forenames: "ENAYET", surname: "Sarwar",  multiWord: true }
 *   "Quyyum"        -> { forenames: "",       surname: "Quyyum",  multiWord: false }
 *
 * The surname is the last word. A full stop ends a word even with no space after it, so
 * "M.Tariq" still separates. The surname keeps its case, because segment 7 is compared
 * with it exactly. The forenames are letters only and upper-cased, so "M. Tariq",
 * "M.Tariq" and "m Tariq" are one tutor while "Tariq" and "M. Tariq" are two.
 *
 * multiWord is true when a whole word, not an initial, sits before the surname. A surname
 * must be one word, so "D. Di Emidio" cannot say whether it means "Emidio" or "DiEmidio".
 * @param {*} value - the column E cell
 * @returns {{forenames: string, surname: string, multiWord: boolean}} empty strings and
 *   false for a blank cell
 */
export const tutorName = (value) => {
  const words = cellText(value)
    .replace(/\./g, ". ")
    .split(/\s+/)
    .filter((word) => word.replace(/\./g, "") !== "");
  if (words.length === 0) return { forenames: "", surname: "", multiWord: false };

  const before = words.slice(0, -1);
  return {
    forenames: before.join("").replace(/[^A-Za-z]/g, "").toUpperCase(),
    surname: words[words.length - 1].replace(/\./g, ""),
    multiWord: before.some((word) => !isInitial(word)),
  };
};

// Month words a category heading may use: the full name, its first three letters, or "Sept".
const headingMonthIndex = (word) => {
  const lower = word.toLowerCase();
  if (lower === "sept") return 8;
  return FULL_MONTHS.findIndex(
    (name) => name === lower || (lower.length === 3 && name.slice(0, 3) === lower)
  );
};

/**
 * The cohorts a category heading names, written the way segment 5 writes them.
 *
 *   "February 2024 BA (Hons) - Level 5 - Sem 2" -> ["Feb24"]
 *   "BMS HND - February 2023"                   -> ["Feb23"]
 *   "April/June 2023 Cohort BA (Hons)"          -> ["Apr23", "Jun23"]
 *   "Sept 2023 - BSc (Hons) - Level 4"          -> ["Sep23"]
 *   "DET"                                       -> []
 *
 * Uses the first four-digit year that has month words straight before it. "/", "&" and
 * "and" may sit between the months.
 * @param {string} heading - the category heading text
 * @returns {string[]} empty when the heading names no month and year
 */
export const headingCohorts = (heading) => {
  const words = String(heading || "").split(/[^A-Za-z0-9]+/).filter(Boolean);

  for (let i = 0; i < words.length; i++) {
    if (!/^(19|20)\d{2}$/.test(words[i])) continue;

    const months = [];
    for (let j = i - 1; j >= 0; j--) {
      const index = headingMonthIndex(words[j]);
      if (index !== -1) months.unshift(MONTH_ABBR[index]);
      else if (words[j].toLowerCase() !== "and") break;
    }
    if (months.length > 0) return months.map((month) => month + words[i].slice(2));
  }

  return [];
};

/** Segment 5 is valid when it is three month letters plus a two-digit year. */
export const isValidCohort = (segment) => {
  const match = COHORT_PATTERN.exec(String(segment));
  if (!match) return false;
  const word = match[1];
  const capitalised = word[0].toUpperCase() + word.slice(1).toLowerCase();
  return MONTH_ABBR.indexOf(capitalised) !== -1;
};

/** Segment 8 is valid when it is a 1-2 letter code, a two-digit year and a group letter. */
export const isValidSession = (segment) => SESSION_PATTERN.test(String(segment));

/**
 * The shared value parser behind both the cohort fix and the session fix.
 *
 * Returns { month, year, group } or null. Null means "cannot be determined with
 * certainty" and is always reported rather than written - a wrong automated rewrite of
 * a course name is far more damaging than an unfixed row.
 *
 *   "April-25" -> { month: "Apr", year: "25", group: "" }
 *   "Apr2626"  -> { month: "Apr", year: "26", group: "" }   (year typed twice)
 *   "Apr2025"  -> { month: "Apr", year: "25", group: "" }
 *   "M25"      -> null   (March or May?)
 *   "Ju25"     -> null   (June or July?)
 */
export const parseValue = (raw) => {
  const text = String(raw === null || raw === undefined ? "" : raw).trim().replace(SEPARATORS, "");
  const match = /^([A-Za-z]+)(\d+)([A-Za-z]?)$/.exec(text);
  if (!match) return null;

  const [, letters, digits, group] = match;

  let month = null;
  if (letters.length <= 2) {
    // One or two letters must be an exact session code. Anything else of that length
    // ("M", "Ju") is ambiguous and must not fall through to month-prefix matching.
    month = CODE_TO_MONTH[letters.toUpperCase()] || null;
  } else {
    const lower = letters.toLowerCase();
    let hits = FULL_MONTHS.filter((name) => name.indexOf(lower) === 0);
    if (hits.length !== 1) {
      // Not a unique prefix, so fall back to the first three letters. This catches input
      // that is longer or misspelt past the prefix, e.g. "junne" -> "jun".
      const head = lower.slice(0, 3);
      hits = FULL_MONTHS.filter((name) => name.slice(0, 3) === head);
    }
    if (hits.length === 1) month = hits[0][0].toUpperCase() + hits[0].slice(1, 3);
  }

  let year = null;
  if (digits.length === 2) {
    year = digits;
  } else if (digits.length === 4) {
    // Equal halves first: "2626" is the two-digit year typed twice, not the year 2626.
    if (digits.slice(0, 2) === digits.slice(2)) year = digits.slice(0, 2);
    else if (/^(19|20)/.test(digits)) year = digits.slice(2);
  }

  if (!month || !year) return null;
  return { month, year, group: group ? group.toUpperCase() : "" };
};

/** A corrected sample for an error message, derived when possible. */
export const cohortHint = (raw) => {
  const parsed = parseValue(raw);
  return parsed ? `${parsed.month}${parsed.year}` : "Feb26";
};

/** A corrected sample for a session error message, derived when possible. */
export const sessionHint = (raw) => {
  const parsed = parseValue(raw);
  if (!parsed) return "F26A";
  const code = MONTH_TO_CODE[parsed.month.toLowerCase()];
  if (!code) return "F26A";
  return `${code}${parsed.year}${parsed.group || "A"}`;
};

// Words that must always appear one specific way. British spellings are the canonical
// entries, so American ones are corrected as near-misses and need no separate rule.
// Extending the feature later is a matter of adding a word here and nothing else.
export const PROTECTED_WORDS = [
  "English",
  "Organisation",
  "Organisations",
  "Organisational",
  "Behaviour",
  "Behavioural",
  "Centre",
];

/**
 * Resolve one word to its protected spelling, or null when it is not one of them.
 *
 * Nearest-wins with a tie broken to null - NOT "exactly one candidate within the
 * threshold". "organization" is distance 1 from "organisation" and distance 2 from
 * "organisations", so the naive rule would reject the single most important case as
 * ambiguous.
 */
export const resolveProtectedWord = (word) => {
  const lower = word.toLowerCase();

  const exact = PROTECTED_WORDS.find((entry) => entry.toLowerCase() === lower);
  if (exact) return exact; // also fixes capitalisation: "english" -> "English"

  // "English" needs 2: "engilsh" is a transposition, which costs 2 in plain Levenshtein.
  const threshold = word.length >= 6 ? 2 : 1;

  const scored = PROTECTED_WORDS.filter(
    (entry) =>
      entry[0].toLowerCase() === lower[0] && Math.abs(entry.length - word.length) <= 1
  )
    .map((entry) => ({ entry, distance: levenshtein(lower, entry.toLowerCase()) }))
    .filter((candidate) => candidate.distance <= threshold);

  if (scored.length === 0) return null;

  const best = Math.min.apply(null, scored.map((candidate) => candidate.distance));
  const winners = scored.filter((candidate) => candidate.distance === best);
  return winners.length === 1 ? winners[0].entry : null;
};

/**
 * Apply protected spellings to every alphabetic run in a module name.
 * Operating on alpha runs only means spacing, punctuation, digits and separators all
 * survive untouched: "Engilsh, Culture & Presentation" keeps its comma and ampersand.
 */
export const applyProtectedWordFixes = (moduleName) =>
  String(moduleName).replace(/[A-Za-z]+/g, (run) => {
    const resolved = resolveProtectedWord(run);
    if (!resolved) return run;
    // A shouted name stays shouted.
    if (run === run.toUpperCase() && run.length > 1) return resolved.toUpperCase();
    return resolved;
  });
